import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  selectIsSavingWorkingProject,
  selectWorkingProjectRegistrationQuestions,
  selectWorkingProjectRegistrationSettings,
  selectWorkingProjectRegistrationSteps,
  selectWorkingProjectRequiredRegistrationQuestions,
} from '@/store/slices/project/selectors';
import { getRegistrationQuestions } from '@/store/slices/channel/selectors';
import { selectFeatureFlag } from '@/store/slices/feature-flags/selectors';
import { FeatureFlagsEnum } from '@/store/slices/feature-flags/types';
import useLoadRegistrationQuestions from '@/connection/hooks/use-load-registration-questions';
import { GateTypes } from '@/types/registration.types';
import DomainGateTypeOption from './domain-gate-type-option';
import PasscodeTypeOption from './passcode-type-option';
import deriveActiveGateTypes from './helpers/derive-active-gate-types';
import resolveGateToggle from './helpers/resolve-gate-toggle';
import { saveWorkingProjectByFields } from '@/store/slices/project';
import { deriveRegistrationUpdate } from '../../helpers/derive-registration-update';
import useOnFinishSave from '@/utils/use-on-finish-save';
import ViewAudienceListsLink from './view-audience-lists-link';
import SettingsToggle from '../../../../controls/settings-toggle';
import SlideDownCard from '@ui/cards/slide-down-card/slide-down-card';

const GatingRegistrationSettings = memo(() => {
  useLoadRegistrationQuestions();

  const isSavingProject = useAppSelector(selectIsSavingWorkingProject);
  const registrationSettings = useAppSelector(selectWorkingProjectRegistrationSettings);
  const registrationSteps = useAppSelector(selectWorkingProjectRegistrationSteps);
  const registrationQuestions = useAppSelector(selectWorkingProjectRegistrationQuestions);
  const requiredRegistrationQuestions = useAppSelector(selectWorkingProjectRequiredRegistrationQuestions);
  const channelRegistrationQuestions = useAppSelector(getRegistrationQuestions);
  const activeGateTypes = useMemo(() => deriveActiveGateTypes(registrationSettings), [registrationSettings]);
  const requirementsMap = registrationSettings?.requirements;
  const sourceBlockedDomains = registrationSettings?.blockedDomainEmails;
  const sourceAllowDomains = requirementsMap?.[GateTypes.domain_email]?.allowedDomains;
  const sourceDomainTab: 'allow' | 'block' =
    registrationSettings?.domain_gate_mode ??
    (sourceAllowDomains?.length ? 'allow' : sourceBlockedDomains?.length ? 'block' : 'allow');

  const { saving: isSavingGatingSettings } = useOnFinishSave({ saveId: 'gating-settings' });

  // Active gates are a SET so legacy events-3 data with multiple coexisting gates (e.g. audience
  // list + blocked domains) all render "on". brandlive-platform persists only one gate, so the
  // first manual toggle collapses the set to a single gate (see resolveGateToggle / saveGateType).
  const [activeGates, setActiveGates] = useState<GateTypes[]>(activeGateTypes);

  useEffect(() => {
    setActiveGates(activeGateTypes);
  }, [activeGateTypes]);

  const isActive = useCallback((gate: GateTypes) => activeGates.includes(gate), [activeGates]);

  const dispatch = useAppDispatch();

  const saveGateType = useCallback(
    (newAccess: GateTypes | null) => {
      if (!registrationSettings) return;
      const update = deriveRegistrationUpdate(
        {
          registration_settings: registrationSettings,
          registration_steps: registrationSteps,
          registration_questions: registrationQuestions,
          required_registration_questions: requiredRegistrationQuestions,
        },
        {
          kind: 'gate',
          gateType: newAccess,
          domainGateMode: sourceDomainTab,
          allowDomains: sourceAllowDomains || [],
          blockDomains: sourceBlockedDomains || [],
          sharedPasscode: requirementsMap?.[GateTypes.shared_passcode]?.sharedPasscode || '',
        },
        channelRegistrationQuestions
      );
      dispatch(saveWorkingProjectByFields({ saveId: 'gating-settings', ...update }));
    },
    [
      channelRegistrationQuestions,
      dispatch,
      registrationQuestions,
      registrationSettings,
      registrationSteps,
      requiredRegistrationQuestions,
      requirementsMap,
      sourceAllowDomains,
      sourceBlockedDomains,
      sourceDomainTab,
    ]
  );

  // The backend only enforces audience-list membership for this app's lists under V2 gating,
  // routed by the global platform.audience_list_members flag. With it off, an audience-gated
  // event rejects every registrant (the V1 fallback reads legacy storage), so turning the
  // gate ON is blocked. Turning it OFF stays possible. Same guard as SsoAudienceListGate —
  // see its doc comment for why the guard is duplicated and the active operands differ.
  const audienceListMembersFlagOn = useAppSelector(selectFeatureFlag(FeatureFlagsEnum.audience_list_members));
  const audienceDisabledByFlag = !audienceListMembersFlagOn && !isActive(GateTypes.passcode_list);

  const handleGateTypeToggle = useCallback(
    (sKey: string, on: boolean) => {
      const gate = Number(sKey) as GateTypes;
      // While the flag is off, passcode_list must never be AUTHORED as the surviving gate:
      // toggling another legacy gate off could otherwise promote it via survivor selection,
      // bypassing the toggle's own OFF→ON block. Drop it from the candidates so the survivor
      // falls through to the next gate (or null).
      const survivorCandidates = audienceListMembersFlagOn
        ? activeGates
        : activeGates.filter((g) => g !== GateTypes.passcode_list);
      const nextSingle = resolveGateToggle(survivorCandidates, gate, on);
      setActiveGates(nextSingle === null ? [] : [nextSingle]);
      saveGateType(nextSingle);
    },
    [activeGates, audienceListMembersFlagOn, saveGateType]
  );

  const isDisabled = isSavingProject || isSavingGatingSettings;

  return (
    <>
      <div className="flex-column gap-8">
        <SettingsToggle
          label="Audience List"
          value={isActive(GateTypes.passcode_list)}
          sKey={GateTypes.passcode_list.toString()}
          onToggle={handleGateTypeToggle}
          disabled={isDisabled || audienceDisabledByFlag}
          tooltipText="Only allow users added to the audience tab to view"
          switchTooltipText={
            audienceDisabledByFlag ? 'Audience list gating is not currently available on BrandTV.' : ''
          }
          gap="0"
        >
          <SlideDownCard autoGrow animateOnInitialLoad={false} open={isActive(GateTypes.passcode_list)}>
            <ViewAudienceListsLink show={true} />
          </SlideDownCard>
        </SettingsToggle>

        <SettingsToggle
          label="Password Login"
          value={isActive(GateTypes.password)}
          sKey={GateTypes.password.toString()}
          onToggle={handleGateTypeToggle}
          disabled={isDisabled}
          tooltipText="Require users to create a username & password to join"
        />

        <DomainGateTypeOption
          isSelected={isActive(GateTypes.domain_email)}
          onToggle={(on) => handleGateTypeToggle(GateTypes.domain_email.toString(), on)}
          disabled={isDisabled}
        />

        <PasscodeTypeOption
          isSelected={isActive(GateTypes.shared_passcode)}
          onToggle={(on) => handleGateTypeToggle(GateTypes.shared_passcode.toString(), on)}
          disabled={isDisabled}
        />
      </div>
    </>
  );
});

GatingRegistrationSettings.displayName = 'GatingRegistrationSettings';

export default GatingRegistrationSettings;
